import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '10px 14px' }}>
      <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '6px' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: '13px', margin: '2px 0' }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('es-ES', { maximumFractionDigits: 1 }) : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function AnalisisPage() {
  const { apiFetch } = useAuth();
  const { t } = useLang();
  const [trends, setTrends]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/dashboard/trends').then(r => r.json()).then(d => setTrends(d)).catch(() => {}).finally(() => setLoading(false));
  }, [apiFetch]);

  // Compute extra metrics from trends
  const correctRate = trends.length > 0 ? trends.map(d => ({
    date: d.date?.slice(5),
    tasa: d.total > 0 ? Math.round((d.correctos / d.total) * 100) : 0,
    autoPct: d.total > 0 ? Math.round((d.auto_cerrados / d.total) * 100) : 0,
  })) : [];

  const avgHours = trends.map(d => ({
    date: d.date?.slice(5),
    horas: d.total > 0 ? Math.round((d.horas / d.total) * 100) / 100 : 0,
    total: d.total,
  }));

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{t('analisis')}</h1>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Análisis de tendencias últimos 14 días</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '80px' }}>Cargando análisis...</div>
      ) : trends.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '80px' }}>
          <p>Sin datos de análisis aún.</p>
          <p style={{ fontSize: '13px', color: '#334155' }}>Ejecuta una sincronización para obtener datos.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Fichajes por día */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px' }}
          >
            <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '20px' }}>Volumen de fichajes por día</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={d => d?.slice(5) || ''} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
                <Bar dataKey="correctos" name="Correctos" fill="#22c55e" stackId="a" />
                <Bar dataKey="auto_cerrados" name="Auto-cerrados" fill="#f97316" stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Tasa de correctos */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px' }}
          >
            <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '20px' }}>Tasa de fichajes correctos (%)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={correctRate}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
                <Line type="monotone" dataKey="tasa" name="% Correctos" stroke="#22c55e" strokeWidth={2.5} dot={{ fill: '#22c55e', r: 3 }} />
                <Line type="monotone" dataKey="autoPct" name="% Auto-cerrados" stroke="#f97316" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Media de horas */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px' }}
          >
            <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '20px' }}>Media de horas por empleado</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={avgHours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `${v}h`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
                <Line type="monotone" dataKey="horas" name="Horas/empleado" stroke="#60a5fa" strokeWidth={2.5} dot={{ fill: '#60a5fa', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px' }}
          >
            <h3 style={{ fontFamily: 'Space Grotesk', fontSize: '15px', fontWeight: 600, color: '#f1f5f9', marginBottom: '16px' }}>Resumen del período</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              {[
                { label: 'Total fichajes', value: trends.reduce((s, d) => s + (d.total || 0), 0).toLocaleString('es-ES'), color: '#60a5fa' },
                { label: 'Correctos', value: trends.reduce((s, d) => s + (d.correctos || 0), 0).toLocaleString('es-ES'), color: '#22c55e' },
                { label: 'Auto-cerrados', value: trends.reduce((s, d) => s + (d.auto_cerrados || 0), 0).toLocaleString('es-ES'), color: '#f97316' },
                { label: 'Días con datos', value: trends.length, color: '#a78bfa' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', padding: '14px 16px' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '6px' }}>{s.label}</div>
                  <div style={{ color: s.color, fontSize: '20px', fontWeight: 700, fontFamily: 'Space Grotesk', fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
